import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

import { SessionService } from '../../core/session/session.service';
import { SidebarComponent } from './sidebar.component';
import { TopBarComponent } from './top-bar.component';
import { ViewportService } from './viewport.service';

/**
 * The app shell, matching the Blazor portal's chrome: a full-width TOP BAR, and beneath it a SIDEBAR
 * beside the routed page.
 *
 * It is NOT an ion-split-pane. That component's model is "menu beside content, both full height",
 * which puts the sidebar level with the top bar rather than under it, and it only collapses below its
 * breakpoint — so a user on a wide screen could never reclaim the space. A plain flex shell gives us
 * both: the bar spans the full width, and the hamburger collapses the sidebar at ANY width.
 *
 * Two behaviours, one piece of markup:
 *  - wide screens: the sidebar is in the flow, and collapsing it gives the page the space;
 *  - narrow screens: it overlays the page with a backdrop, and a tap closes it (a 16rem sidebar next
 *    to a phone-width page would leave nothing for the page).
 *
 * This owns only WHETHER the sidebar shows. Its width, its menu, and which categories are collapsed
 * belong to SidebarComponent.
 */
@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, TopBarComponent],
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    /* A routed page is .ion-page — position: absolute; inset: 0 — so it sizes against the nearest
       positioned ancestor. #page is that ancestor; without the relative positioning every page
       collapses to zero height. */
    #page {
      position: relative;
      flex: 1;
      min-width: 0;
    }
  `,
  template: `
    <app-top-bar [sidebarOpen]="open()" (toggleSidebar)="toggle()" />

    <div class="relative flex min-h-0 flex-1">
      @if (open()) {
        <!-- Narrow screens only: tapping the backdrop closes the overlaying sidebar. -->
        <div
          class="absolute inset-0 z-30 bg-black/30 md:hidden"
          aria-hidden="true"
          (click)="close()"
        ></div>
      }

      <app-sidebar [open]="open()" (navigated)="closeOnNarrow()" />

      <div id="page">
        <!-- Keyed on the ACTIVE CLIENT. Switching client re-themes the shell and re-fetches the
             session, but the page inside was already built: every list loads in its constructor, so
             it goes on showing the previous client's rows until you hit refresh. Re-keying the outlet
             destroys the routed component and re-creates it, which re-runs those loads against the new
             client — the same thing a manual refresh did, without the reload. -->
        @for (client of [activeClientId()]; track client) {
          <router-outlet />
        }
      </div>
    </div>
  `,
})
export class MainLayoutComponent {
  private readonly session = inject(SessionService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly activeClientId = computed(
    () => this.session.session()?.activeClientId ?? null,
  );

  /** Tracks the breakpoint live, so a resize behaves like a fresh load at that width. */
  private readonly wide = inject(ViewportService).isWide;

  /**
   * The two states are kept SEPARATE on purpose.
   *
   * `desktopOpen` is the user's preference for the docked sidebar; `drawerOpen` is the transient
   * overlay on a phone. Collapsing on a phone must not wipe out the choice you made on a wide screen —
   * so when the window grows back, the sidebar returns exactly as you left it, and when it shrinks,
   * the drawer is always closed rather than left covering the page.
   */
  private readonly desktopOpen = signal(true);
  private readonly drawerOpen = signal(false);

  protected readonly open = computed(() => (this.wide() ? this.desktopOpen() : this.drawerOpen()));

  constructor() {
    // A deep link (or a redirect) that lands elsewhere must not leave the overlay open on a phone.
    const routes = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.closeOnNarrow());

    // Crossing the breakpoint re-runs the same rule as a fresh load at that width: below md the
    // drawer is always closed (it would otherwise sit over the page, and the user never opened it
    // *as a drawer*); above md the docked sidebar returns to whatever the user chose.
    effect(() => {
      if (!this.wide()) this.drawerOpen.set(false);
    });

    this.destroyRef.onDestroy(() => routes.unsubscribe());
  }

  protected toggle(): void {
    if (this.wide()) {
      this.desktopOpen.update((open) => !open);
    } else {
      this.drawerOpen.update((open) => !open);
    }
  }

  /** The backdrop only exists on a phone, so this is always the drawer. */
  protected close(): void {
    this.drawerOpen.set(false);
  }

  /** A tap on a link dismisses the overlaying drawer. On a wide screen the sidebar is docked beside
   * the page and stays exactly as the user left it. */
  protected closeOnNarrow(): void {
    if (!this.wide()) this.drawerOpen.set(false);
  }
}
